import React, { useEffect, useState } from 'react';
import { useMingo } from '../context/MingoContext';
import { isRegionDebugEnabled } from '../lib/regionDebug';

const LS_KEYS = ['mingo_gps_slot', 'mingo_pick_slot', 'mingo_active'];

function readLsSnapshot() {
  /** @type {Record<string, string | null>} */
  const out = {};
  for (const k of LS_KEYS) {
    try {
      const v = window.localStorage.getItem(k);
      out[k] = v && v.length > 200 ? `${v.slice(0, 200)}…` : v;
    } catch {
      out[k] = '(read error)';
    }
  }
  return out;
}

function slotJson(slot) {
  if (!slot) return '(null)';
  try {
    const s = JSON.stringify(slot);
    return s.length > 120 ? `${s.slice(0, 120)}…` : s;
  } catch {
    return '(error)';
  }
}

const BOX_STYLE = {
  margin: '0 0 10px',
  padding: '8px 10px',
  fontSize: 10,
  lineHeight: 1.45,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  background: '#fffbeb',
  border: '1px solid #f59e0b',
  borderRadius: 8,
  color: '#78350f',
  wordBreak: 'break-all',
  whiteSpace: 'pre-wrap',
};

const COMPACT_STYLE = {
  ...BOX_STYLE,
  margin: '4px 12px 0',
  fontSize: 9,
  maxHeight: 120,
  overflow: 'auto',
};

/**
 * @param {{ compact?: boolean; active?: boolean }} props
 */
export function RegionStateDebugBox({ compact = false, active = true }) {
  const {
    gpsSlot,
    pickSlot,
    activeSource,
    gpsSlotLabel,
    pickSlotLabel,
    selectedRegions,
    userCoords,
  } = useMingo();
  const [ls, setLs] = useState(() => readLsSnapshot());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isRegionDebugEnabled() || !active) return undefined;
    const refresh = () => {
      setLs(readLsSnapshot());
      setTick((t) => t + 1);
    };
    refresh();
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refresh);
    const id = window.setInterval(refresh, 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', refresh);
      window.clearInterval(id);
    };
  }, [active]);

  if (!isRegionDebugEnabled() || !active) return null;

  const lat =
    gpsSlot?.lat != null
      ? Number(gpsSlot.lat).toFixed(4)
      : userCoords?.lat != null
        ? Number(userCoords.lat).toFixed(4)
        : '?';
  const lng =
    gpsSlot?.lng != null
      ? Number(gpsSlot.lng).toFixed(4)
      : userCoords?.lng != null
        ? Number(userCoords.lng).toFixed(4)
        : '?';

  const headerPick = pickSlotLabel || '(empty label)';
  const shim1 = selectedRegions?.[1] || '(empty)';

  if (compact) {
    const lines = [
      `[DBG #${tick}]`,
      `pickSlot(state)=${slotJson(pickSlot)}`,
      `gpsSlot=${slotJson(gpsSlot)}`,
      `activeSource=${activeSource}`,
      `pickLabel=${pickSlotLabel || '-'} | header2=${headerPick}`,
      `LS.pick=${ls.mingo_pick_slot || '-'}`,
    ];
    return (
      <div style={COMPACT_STYLE} aria-hidden="true">
        {lines.join('\n')}
      </div>
    );
  }

  const lines = [
    `[DEBUG #${tick}]`,
    `--- React state ---`,
    `gpsSlot(state) = ${slotJson(gpsSlot)}`,
    `pickSlot(state) = ${slotJson(pickSlot)}`,
    `activeSource = ${activeSource || '(empty)'}`,
    `gpsSlotLabel = ${gpsSlotLabel || '(empty)'}`,
    `pickSlotLabel = ${pickSlotLabel || '(empty)'}`,
    `selectedRegions[1] (shim) = ${shim1}`,
    `header slot2 uses pickSlotLabel = ${headerPick}`,
    `gps.coords = ${lat}, ${lng}`,
    `--- localStorage ---`,
    `mingo_gps_slot = ${ls.mingo_gps_slot || '(empty)'}`,
    `mingo_pick_slot = ${ls.mingo_pick_slot || '(empty)'}`,
    `mingo_active = ${ls.mingo_active || '(empty)'}`,
  ];

  return (
    <div style={BOX_STYLE} aria-hidden="true">
      {lines.join('\n')}
    </div>
  );
}
